import { renderRefusal } from "../view/refusal.js";
import { plotPaths, plotMarkers, MODEL_SYMBOLS } from "../view/plot.js";
/**
 * Graphe de comparaison, en SVG écrit à la main.
 *
 * Aucune bibliothèque de graphiques : la règle « la nature d'une série passe
 * par la forme, jamais par la seule couleur » (SPEC §3.4) est plus facile à
 * tenir en écrivant le balisage qu'en pliant une bibliothèque à sa volonté.
 * Chaque série porte un `data-nature` — c'est lui qui commande la forme, et il
 * est vérifiable par un test.
 *
 * Le graphe s'annonce comme une image accompagnée d'un titre et d'une
 * description ; la table qui l'accompagne (`eratosthenes-table.ts`) porte la
 * même information, sans exception.
 */

import type {
  ComparedObservation,
  ShadowComparison,
  ShadowModelComparison,
} from "../core/comparison.js";
import { valueIn } from "../core/units.js";
import { fr } from "../i18n/fr.js";
import { escape, escapeAttribute, formatDayMonth, formatInstant, formatNumber } from "../view/format.js";
import { linearTicks, sampleIndices, tickDecimals } from "../view/axis.js";

const WIDTH = 720;
const HEIGHT = 380;
const MARGIN = { top: 32, right: 24, bottom: 48, left: 64 } as const;
/** Bornes de lisibilité, pas de préférence : au-delà, les libellés se recouvrent. */
const X_TICKS = 7;
const Y_TICKS = 5;

const PLOT_WIDTH = WIDTH - MARGIN.left - MARGIN.right;
const PLOT_HEIGHT = HEIGHT - MARGIN.top - MARGIN.bottom;

interface Scale {
  readonly toX: (julianDay: number) => number;
  readonly toY: (metres: number) => number;
  readonly minMetre: number;
  readonly maxMetre: number;
}


function scaleOf(comparison: Extract<ShadowComparison, { ok: true }>): Scale | undefined {
  const days: number[] = [];
  const metres: number[] = [];

  for (const observation of comparison.observations) {
    days.push(valueIn(observation.instant, "d"));
    metres.push(valueIn(observation.observed, "m") + valueIn(observation.uncertainty, "m"));
    metres.push(valueIn(observation.observed, "m") - valueIn(observation.uncertainty, "m"));
    for (const prediction of observation.predictions.values()) {
      if (prediction.kind === "predicted") metres.push(valueIn(prediction.value, "m"));
    }
  }

  if (days.length === 0) return undefined;

  const minDay = days.reduce((a, b) => Math.min(a, b), Infinity);
  const maxDay = days.reduce((a, b) => Math.max(a, b), -Infinity);
  const minMetre = metres.reduce((a, b) => Math.min(a, b), 0);
  const maxMetre = metres.reduce((a, b) => Math.max(a, b), -Infinity);

  const daySpan = maxDay - minDay || 1;
  const metreSpan = maxMetre - minMetre || 1;
  if (![minDay, maxDay, minMetre, maxMetre, daySpan, metreSpan].every(Number.isFinite)) return undefined;

  return {
    toX: (day) => MARGIN.left + ((day - minDay) / daySpan) * PLOT_WIDTH,
    toY: (metre) =>
      MARGIN.top + PLOT_HEIGHT - ((metre - minMetre) / metreSpan) * PLOT_HEIGHT,
    minMetre,
    maxMetre,
  };
}

/** Mesures : points et barres d'erreur. Jamais une courbe. */
function measuredGroup(observations: readonly ComparedObservation[], scale: Scale): string {
  const marks = observations
    .map((observation) => {
      const x = scale.toX(valueIn(observation.instant, "d"));
      const value = valueIn(observation.observed, "m");
      const sigma = valueIn(observation.uncertainty, "m");
      const top = scale.toY(value + sigma);
      const bottom = scale.toY(value - sigma);
      return [
        `<line data-role="error-bar" x1="${x.toFixed(2)}" y1="${top.toFixed(2)}"`,
        ` x2="${x.toFixed(2)}" y2="${bottom.toFixed(2)}" />`,
        `<circle cx="${x.toFixed(2)}" cy="${scale.toY(value).toFixed(2)}" r="3.5" />`,
      ].join("");
    })
    .join("");

  return `<g data-nature="measured" class="verlet-series verlet-series--measured">${marks}</g>`;
}

/** Prédictions : trait plein continu. */
function predictedGroup(
  model: ShadowModelComparison,
  observations: readonly ComparedObservation[],
  scale: Scale,
  rank: number,
): string {
  const sites = new Map<string, ComparedObservation[]>();
  for (const observation of observations) {
    const group = sites.get(observation.siteId) ?? [];
    group.push(observation);
    sites.set(observation.siteId, group);
  }
  const paths = [...sites.values()].map((group) => {
    const points = [...group].sort((a, b) => valueIn(a.instant, "d") - valueIn(b.instant, "d"))
      .map((observation) => {
        const prediction = observation.predictions.get(model.model.id);
        return { x: valueIn(observation.instant, "d"), y: prediction?.kind === "predicted" ? valueIn(prediction.value, "m") : undefined };
      });
    return plotPaths(points, scale) + plotMarkers(points, rank, scale);
  }).join("");
  return `<g data-nature="predicted" data-model="${escapeAttribute(model.model.id)}" data-series="${rank}" class="verlet-series verlet-series--predicted">${paths}</g>`;
}

function legend(comparison: Extract<ShadowComparison, { ok: true }>): string {
  const entries = [
    `<li data-nature="measured">${escape(fr.seriesMeasured)}</li>`,
    // Le symbole reprend celui de la courbe : la couleur ne porte rien seule.
    ...comparison.models
      .filter((model) => model.calculation === "computed")
      .map(
        (model, rank) =>
          `<li data-nature="predicted" data-model="${escapeAttribute(model.model.id)}" data-series="${rank}">${MODEL_SYMBOLS[rank % MODEL_SYMBOLS.length]} ${escape(
            model.model.label,
          )}</li>`,
      ),
  ];
  return `<ul class="verlet-legend">${entries.join("")}</ul>`;
}

function axes(scale: Scale, comparison: Extract<ShadowComparison, { ok: true }>): string {
  const seen = new Set<string>();
  const instants = comparison.observations
    .map((observation) => observation.instant)
    .filter((instant) => {
      const label = formatInstant(instant);
      if (seen.has(label)) return false;
      seen.add(label);
      return true;
    })
    .sort((a, b) => valueIn(a, "d") - valueIn(b, "d"));

  // Un relevé par jour pendant un an étiquetterait trois cent soixante-cinq
  // dates sur six cent trente unités. Le nombre de graduations est borné.
  const ticks = sampleIndices(instants.length, X_TICKS)
    .map((index) => instants[index])
    .filter((instant): instant is NonNullable<typeof instant> => instant !== undefined)
    .map((instant) => {
      const x = scale.toX(valueIn(instant, "d"));
      return [
        `<text x="${x.toFixed(2)}" y="${(MARGIN.top + PLOT_HEIGHT + 20).toFixed(2)}"`,
        ` text-anchor="middle" class="verlet-tick">${escape(formatDayMonth(instant))}</text>`,
      ].join("");
    })
    .join("");

  const decimals = tickDecimals(scale.maxMetre - scale.minMetre);
  const yTicks = linearTicks(scale.minMetre, scale.maxMetre, Y_TICKS)
    .map((metre) => {
      const y = scale.toY(metre);
      return [
        `<line x1="${MARGIN.left}" y1="${y.toFixed(2)}"`,
        ` x2="${MARGIN.left + PLOT_WIDTH}" y2="${y.toFixed(2)}" class="verlet-gridline" />`,
        `<text x="${MARGIN.left - 8}" y="${(y + 4).toFixed(2)}" text-anchor="end"`,
        ` class="verlet-tick">${escape(formatNumber(metre, decimals))}</text>`,
      ].join("");
    })
    .join("");

  return [
    `<g class="verlet-axes">`,
    yTicks,
    `<line x1="${MARGIN.left}" y1="${MARGIN.top}" x2="${MARGIN.left}" y2="${MARGIN.top + PLOT_HEIGHT}" />`,
    `<line x1="${MARGIN.left}" y1="${MARGIN.top + PLOT_HEIGHT}" x2="${MARGIN.left + PLOT_WIDTH}" y2="${MARGIN.top + PLOT_HEIGHT}" />`,
    `<text x="8" y="${MARGIN.top - 12}" class="verlet-axis-label">${escape(fr.axisShadowLength)}</text>`,
    ticks,
    `</g>`,
  ].join("");
}

function description(comparison: Extract<ShadowComparison, { ok: true }>): string {
  const count = valueIn(comparison.observationCount, "1");
  const parts = comparison.models
    .filter((model) => model.meanAbsoluteSigma !== undefined)
    .map(
      (model) =>
        `${model.model.label} : ${formatNumber(valueIn(model.meanAbsoluteSigma!, "1"), 1)} σ`,
    );
  return `${fr.chartDescription(count)} ${parts.join(" · ")}`;
}

export function renderComparisonChart(comparison: ShadowComparison): string {
  if (!comparison.ok) return renderRefusal(comparison.reasons);

  const scale = scaleOf(comparison);
  if (scale === undefined) return renderRefusal([fr.noPlottableObservation]);

  return [
    `<figure class="verlet-chart">`,
    `<div class="verlet-chart__viewport" role="region" tabindex="0"`,
    ` aria-label="${escapeAttribute(fr.chartTitle)}">`,
    `<svg viewBox="0 0 ${WIDTH} ${HEIGHT}" role="img"`,
    ` aria-labelledby="verlet-chart-title verlet-chart-desc">`,
    `<title id="verlet-chart-title">${escape(fr.chartTitle)}</title>`,
    `<desc id="verlet-chart-desc">${escape(description(comparison))}</desc>`,
    axes(scale, comparison),
    ...comparison.models.filter((model) => model.calculation === "computed").map((model, rank) => predictedGroup(model, comparison.observations, scale, rank)),
    measuredGroup(comparison.observations, scale),
    `</svg>`,
    `</div>`,
    legend(comparison),
    `</figure>`,
  ].join("");
}
